/**
 * Attribute configuration for dynamic cohort rules
 * 
 * TODO (Backend): 
 * - Implement option loaders for teams, houses, and university sessions
 * - Provide API endpoints that return minimal payloads for select menus
 * - Ensure data is available in store getters or provide async loaders
 */

import store from '@/store/store';
import _ from 'lodash';

export interface AttributeConfig {
  field: string;
  label: string;
  type: 'select' | 'number' | 'async-select';
  min?: number;
  max?: number;
  operators: OperatorConfig[];
  getOptions?: () => Promise<Array<{ label: string; value: any }>> | Array<{ label: string; value: any }>;
  requiresWindow?: boolean; // For metrics that need "most_recent" window
}

export interface OperatorConfig {
  value: string;
  label: string;
  valueType: 'single' | 'multiple' | 'range';
}

export const ATTRIBUTE_CONFIGS: AttributeConfig[] = [
  {
    field: 'team',
    label: 'Team Name',
    type: 'select',
    operators: [
      { value: 'IN', label: 'is one of', valueType: 'multiple' },
      { value: 'NOT_IN', label: 'is not one of', valueType: 'multiple' },
      { value: 'IS', label: 'is', valueType: 'single' },
      { value: 'IS_NOT', label: 'is not', valueType: 'single' },
    ],
    getOptions: () => {
      // TODO (Backend): Load teams from store or API
      // For now, return empty array - will be populated from store in component
      return [];
    },
  },
  {
    field: 'house',
    label: 'House',
    type: 'select',
    operators: [
      { value: 'IN', label: 'is one of', valueType: 'multiple' },
      { value: 'NOT_IN', label: 'is not one of', valueType: 'multiple' },
      { value: 'IS', label: 'is', valueType: 'single' },
      { value: 'IS_NOT', label: 'is not', valueType: 'single' },
    ],
    getOptions: () => {
      // TODO (Backend): Load houses from store or API
      return [];
    },
  },
  {
    field: 'job_tenure',
    label: 'Job Tenure (days)',
    type: 'number',
    operators: [
      { value: '=', label: 'equals', valueType: 'single' },
      { value: '!=', label: 'does not equal', valueType: 'single' },
      { value: '<', label: 'is less than', valueType: 'single' },
      { value: '<=', label: 'is less than or equal', valueType: 'single' },
      { value: '>', label: 'is greater than', valueType: 'single' },
      { value: '>=', label: 'is greater than or equal', valueType: 'single' },
      { value: 'BETWEEN', label: 'is between', valueType: 'range' },
    ],
  },
  {
    field: 'wellbeing',
    label: 'Well-being Score',
    type: 'number',
    min: 0,
    max: 100,
    requiresWindow: true,
    operators: [
      { value: '=', label: 'equals', valueType: 'single' },
      { value: '!=', label: 'does not equal', valueType: 'single' },
      { value: '<', label: 'is less than', valueType: 'single' },
      { value: '<=', label: 'is less than or equal', valueType: 'single' },
      { value: '>', label: 'is greater than', valueType: 'single' },
      { value: '>=', label: 'is greater than or equal', valueType: 'single' },
      { value: 'BETWEEN', label: 'is between', valueType: 'range' },
    ],
  },
  {
    field: 'enps',
    label: 'eNPS Score',
    type: 'number',
    min: -100,
    max: 100,
    requiresWindow: true,
    operators: [
      { value: '=', label: 'equals', valueType: 'single' },
      { value: '!=', label: 'does not equal', valueType: 'single' },
      { value: '<', label: 'is less than', valueType: 'single' },
      { value: '<=', label: 'is less than or equal', valueType: 'single' },
      { value: '>', label: 'is greater than', valueType: 'single' },
      { value: '>=', label: 'is greater than or equal', valueType: 'single' },
      { value: 'BETWEEN', label: 'is between', valueType: 'range' },
    ],
  },
  {
    field: 'nps',
    label: 'NPS Score',
    type: 'number',
    min: -100,
    max: 100,
    requiresWindow: true,
    operators: [
      { value: '=', label: 'equals', valueType: 'single' },
      { value: '!=', label: 'does not equal', valueType: 'single' },
      { value: '<', label: 'is less than', valueType: 'single' },
      { value: '<=', label: 'is less than or equal', valueType: 'single' },
      { value: '>', label: 'is greater than', valueType: 'single' },
      { value: '>=', label: 'is greater than or equal', valueType: 'single' },
      { value: 'BETWEEN', label: 'is between', valueType: 'range' },
    ],
  },
  {
    field: 'university',
    label: 'University Session',
    type: 'async-select',
    operators: [
      { value: 'IS', label: 'is', valueType: 'single' },
      { value: 'IS_NOT', label: 'is not', valueType: 'single' },
      { value: 'CONTAINS', label: 'contains', valueType: 'single' },
      { value: 'NOT_CONTAINS', label: 'does not contain', valueType: 'single' },
      { value: 'IN', label: 'is one of', valueType: 'multiple' },
      { value: 'NOT_IN', label: 'is not one of', valueType: 'multiple' },
    ],
    getOptions: async () => {
      // TODO (Backend): Load university sessions from store or API
      // Should return array of { label: string, value: string } for session titles
      // const universitySessions = this.$store.getters['membersCohort/universitySessions'];
      const orderBy = ['class_name', 'asc'];
      await store.dispatch('happilyvillageUniversity/getUniversity');
      const options = _.orderBy(store.getters['happilyvillageUniversity/sessions_all'], orderBy[0], orderBy[1]);
      return options.map((option: any) => ({
        label: option.class_name,
        value: option.key,
      }));
    },
  },
  {
    field: 'daily_streak',
    label: 'Daily Streak',
    type: 'number',
    operators: [
      { value: '=', label: 'equals', valueType: 'single' },
      { value: '!=', label: 'does not equal', valueType: 'single' },
      { value: '<', label: 'is less than', valueType: 'single' },
      { value: '<=', label: 'is less than or equal', valueType: 'single' },
      { value: '>', label: 'is greater than', valueType: 'single' },
      { value: '>=', label: 'is greater than or equal', valueType: 'single' },
      { value: 'BETWEEN', label: 'is between', valueType: 'range' },
    ],
  },
];

export function getAttributeConfig(field: string): AttributeConfig | undefined {
  return ATTRIBUTE_CONFIGS.find((config) => config.field === field);
}

export function getAllAttributes(): AttributeConfig[] {
  return ATTRIBUTE_CONFIGS;
}

